import { useCallback, useEffect, useRef } from "react";

import { LiveEditorStep, LiveEditorStepKey } from "../Steps/LiveEditorStep.enum";

const STEP_WRITE_QUIET_MS = 1_000;

interface PendingStep {
  stepIndex: LiveEditorStep;
  stepKey: LiveEditorStepKey;
}

export const useCoalescedStepWrite = (writeStep: (stepKey: LiveEditorStepKey) => Promise<unknown>) => {
  const writeStepRef = useRef(writeStep);
  writeStepRef.current = writeStep;
  const pendingStepRef = useRef<PendingStep | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pendingStep = pendingStepRef.current;
    pendingStepRef.current = null;
    if (pendingStep) {
      void writeStepRef.current(pendingStep.stepKey);
    }
  }, []);

  // Leaving the editor before the quiet delay ends must still save the furthest step.
  useEffect(() => flush, [flush]);

  return useCallback(
    (stepIndex: LiveEditorStep, stepKey: LiveEditorStepKey) => {
      if (!pendingStepRef.current || stepIndex > pendingStepRef.current.stepIndex) {
        pendingStepRef.current = { stepIndex, stepKey };
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(flush, STEP_WRITE_QUIET_MS);
    },
    [flush]
  );
};
